import { Row, Col, message, Table, Pagination, Select, Tag, Input } from "antd";
import styled from "styled-components";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MdVisibility } from "react-icons/md";
import { NavLink, useSearchParams } from "react-router-dom";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { getVendorAmendments } from "store/vendor/vendorSlice";
import { Amendment } from "common/interface/Amendment";
import { routes } from "common/routes";
import { debounce } from "common/utils";

type AmendmentRow = Amendment & { key: string };

const MESSAGE_KEY = "vendor_amendments";

const StyledActionLink = styled(NavLink)`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const statusColorMap: Record<string, string> = {
  PENDING: "gold",
  APPROVED: "green",
  REJECTED: "red",
  CANCELLED: "default",
};

const VendorAmendmentsPage = () => {
  useDocumentTitle("Vendor Requests");
  const dispatch = useAppDispatch();
  const { amendments, amendmentStatus, amendmentError } = useAppSelector(
    (state) => state.vendors
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") ?? "PENDING";
  const requestTypeFilter = searchParams.get("requestType") ?? "";
  const entityIdFilter = searchParams.get("entityId") ?? "";
  const [entityIdInput, setEntityIdInput] = useState(entityIdFilter);

  useEffect(() => {
    setEntityIdInput(entityIdFilter);
  }, [entityIdFilter]);

  const updateEntityIdFilter = useCallback((value: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) {
        params.set("entityId", value);
      } else {
        params.delete("entityId");
      }
      return params;
    });
  }, [setSearchParams]);

  const debouncedUpdateEntityId = useMemo(
    () => debounce((value: string) => updateEntityIdFilter(value.trim()), 400),
    [updateEntityIdFilter]
  );

  const getData = useCallback(
    (pageNum = 1, pageSize = 25) => {
      dispatch(
        getVendorAmendments({
          pageNo: pageNum,
          pageSize,
          status: statusFilter || undefined,
          requestType: requestTypeFilter || undefined,
          entityId: entityIdFilter || undefined,
        })
      );
    },
    [dispatch, statusFilter, requestTypeFilter, entityIdFilter]
  );

  useEffect(() => {
    getData(1, 25);
  }, [getData]);

  useEffect(() => {
    if (amendmentStatus === "loading") {
      message.loading({ content: "Loading", key: MESSAGE_KEY });
    } else if (amendmentStatus === "error" && amendmentError) {
      message.error({ content: amendmentError, key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [amendmentStatus, amendmentError]);

  const rows: AmendmentRow[] = useMemo(
    () =>
      (amendments?.data ?? []).map((item, i) => ({
        ...item,
        key: `amendment-${item.id}-${i}`,
      })),
    [amendments]
  );

  const columns: ColumnsType<AmendmentRow> = [
    { key: "id", title: "Request ID", dataIndex: "id" },
    { key: "requestType", title: "Request Type", dataIndex: "requestType" },
    {
      key: "entityId",
      title: "Vendor ID",
      dataIndex: "entityId",
      render: (value: string | null) => <>{value ?? "-"}</>,
    },
    {
      key: "name",
      title: "Vendor Name",
      render: (_, record) => <>{(record.requestedPayload?.name as string) ?? "-"}</>,
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <Tag color={statusColorMap[status] ?? "default"}>{status}</Tag>
      ),
    },
    { key: "requestedBy", title: "Requested By", dataIndex: "requestedBy" },
    {
      key: "requestedAt",
      title: "Requested At",
      dataIndex: "requestedAt",
      render: (value: string) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      key: "actions",
      title: "Actions",
      render: (_, record) => (
        <StyledActionLink
          to={`/${routes.VENDOR.AMENDMENT_DETAIL.replace(":id", String(record.id))}`}
        >
          <MdVisibility />
        </StyledActionLink>
      ),
    },
  ];

  const Paginator = () => {
    if (amendments && amendments.pageNo !== undefined) {
      const { pageNo, pageSize, total } = amendments;
      return (
        <StyledPagination
          current={pageNo}
          pageSize={pageSize}
          total={total}
          onChange={(page, size) => getData(page, size)}
        />
      );
    }
    return <></>;
  };

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Vendor Requests" />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <div
            className="action-strip__item"
            style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}
          >
            <Input
              placeholder="Vendor ID"
              allowClear
              style={{ width: 160 }}
              value={entityIdInput}
              onChange={(e) => {
                const value = e.target.value;
                setEntityIdInput(value);
                debouncedUpdateEntityId(value);
              }}
            />
            <Select
              value={statusFilter}
              style={{ width: 160 }}
              onChange={(value) => updateFilter("status", value)}
              options={[
                { value: "PENDING", label: "Pending" },
                { value: "APPROVED", label: "Approved" },
                { value: "REJECTED", label: "Rejected" },
                { value: "CANCELLED", label: "Cancelled" },
                { value: "", label: "All" },
              ]}
            />
            <Select
              value={requestTypeFilter || undefined}
              allowClear
              placeholder="Request Type"
              style={{ width: 180 }}
              onChange={(value) => updateFilter("requestType", value ?? "")}
              options={[
                { value: "CREATE", label: "Create" },
                { value: "UPDATE", label: "Update" },
                { value: "TOGGLE_STATUS", label: "Toggle Status" },
              ]}
            />
          </div>
        </AppActionStrip>
      </Row>
      {rows.length ? (
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={rows}
              scroll={{ x: "100vh", y: "65vh" }}
              size="small"
              pagination={false}
              loading={amendmentStatus === "loading"}
              bordered
            />
          </Col>
          <Col xs={24}>
            <Paginator />
          </Col>
        </Row>
      ) : (
        amendmentStatus !== "loading" && <>No vendor requests to show.</>
      )}
    </>
  );
};

export default VendorAmendmentsPage;
